import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
} from "@exhousing/design-system";
import { Pencil, Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { useUpdateProperty } from "@/hooks/api/useProperties";
import { AMENITY_KEYS, INCLUSIVE_KEYS, enabledAmenityLabels } from "@/lib/amenities";
import type { components } from "@/types/api";

type Property = components["schemas"]["PropertyResponse"];

/** Amenities, what's included in the rent, and the free-sector flag — read-only with an
 *  inline Edit→Save/Cancel flow. Saves the compact amenities blob (true flags only). */
export function FeaturesCard({ property }: { property: Property }) {
  const update = useUpdateProperty(property.id);
  const [editing, setEditing] = useState(false);

  const initialAmenities = () => ({ ...(property.amenities ?? {}) }) as Record<string, boolean>;
  const [amenities, setAmenities] = useState<Record<string, boolean>>(initialAmenities);
  const [freeSector, setFreeSector] = useState<boolean>(Boolean(property.free_sector));

  const toggle = (key: string) => setAmenities((a) => ({ ...a, [key]: !a[key] }));

  const cancel = () => {
    setAmenities(initialAmenities());
    setFreeSector(Boolean(property.free_sector));
    setEditing(false);
  };

  const save = () => {
    update.mutate(
      {
        free_sector: freeSector,
        // Keep only the true flags so the JSONB blob stays compact.
        amenities: Object.fromEntries(Object.entries(amenities).filter(([, v]) => v)),
      },
      {
        onSuccess: () => {
          toast.success("Features updated");
          setEditing(false);
        },
        onError: () => toast.error("Could not update features"),
      },
    );
  };

  const { amenities: amenityLabels, inclusive } = enabledAmenityLabels(
    property.amenities ?? {},
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="size-4 text-muted-foreground" />
          Features
        </CardTitle>
        {!editing && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
            <Pencil className="size-4" />
            Edit
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        {editing ? (
          <>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={freeSector}
                onChange={(e) => setFreeSector(e.target.checked)}
                className="size-4 rounded border-input accent-primary"
              />
              Free sector (geliberaliseerd)
            </label>
            <div className="space-y-2 border-t border-border/60 pt-4">
              <Label>Amenities</Label>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
                {AMENITY_KEYS.map(({ key, label }) => (
                  <label key={key} className="flex cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={Boolean(amenities[key])}
                      onChange={() => toggle(key)}
                      className="size-4 rounded border-input accent-primary"
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-2 border-t border-border/60 pt-4">
              <Label>Included in rent</Label>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
                {INCLUSIVE_KEYS.map(({ key, label }) => (
                  <label key={key} className="flex cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={Boolean(amenities[key])}
                      onChange={() => toggle(key)}
                      className="size-4 rounded border-input accent-primary"
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" disabled={update.isPending} onClick={save}>
                {update.isPending ? "Saving…" : "Save"}
              </Button>
              <Button size="sm" variant="ghost" onClick={cancel}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm">
              <span className="text-muted-foreground">Sector: </span>
              {property.free_sector ? "Free (geliberaliseerd)" : "Social"}
            </p>
            {amenityLabels.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {amenityLabels.map((a) => (
                  <Badge key={a} tone="neutral">
                    {a}
                  </Badge>
                ))}
              </div>
            )}
            {inclusive.length > 0 && (
              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Included in rent
                </p>
                <div className="flex flex-wrap gap-2">
                  {inclusive.map((a) => (
                    <Badge key={a} tone="success">
                      {a}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {amenityLabels.length === 0 && inclusive.length === 0 && (
              <p className="text-sm text-muted-foreground">No amenities recorded.</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
